import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBell from '../common/NotificationBell';
import { VipBadge } from '../common/VipBadge';

const SITE_LOGO = '/logo.png';

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); setMenuOpen(false); };

  const navStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 900,
    background: scrolled ? 'rgba(10,9,0,0.97)' : 'rgba(10,9,0,0.75)',
    backdropFilter: 'blur(12px)',
    borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
    transition: 'all 0.3s', height: '70px', display: 'flex', alignItems: 'center',
  };

  const linkStyle = ({ isActive }) => ({
    color: isActive ? '#f5c400' : 'var(--text-secondary)',
    fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '13px',
    letterSpacing: '0.05em', textTransform: 'uppercase', padding: '6px 0',
    borderBottom: isActive ? '2px solid #f5c400' : '2px solid transparent',
    transition: 'all 0.2s', textDecoration: 'none',
  });

  const LogoIcon = () => !logoError
    ? <img src={SITE_LOGO} alt="Apex Gaming" onError={() => setLogoError(true)} style={{ width: 38, height: 38, objectFit: 'contain', borderRadius: 8 }} />
    : <div style={{ width: 36, height: 36, background: 'var(--gradient-primary)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 16, color: '#0a0900' }}>A</div>;

  const navLinks = [
    ['/platforms',  'Platforms'],
    ['/offers',     'Offers'],
    ['/games',      'Mini Games'],
    ['/wheel',      '🎡 Wheel'],
    ['/memberships','Memberships'],
    ['/reviews',    'Reviews'],
    ['/about',      'About'],
    ['/chat',       'Chat'],
  ];

  return (
    <nav style={navStyle}>
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <LogoIcon />
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 18, background: 'var(--gradient-primary)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>APEX</span>
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: 18, color: 'var(--text-secondary)' }}>GAMING</span>
        </Link>

        {/* Desktop Nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 22 }} className="desktop-nav">
          {navLinks.map(([path, label]) => (
            <NavLink key={path} to={path} style={linkStyle}>{label}</NavLink>
          ))}
        </div>

        {/* Auth + Notifications */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} className="desktop-nav">
          {user ? (
            <>
              {isAdmin && <Link to="/admin" className="btn btn-secondary btn-sm">Admin</Link>}
              <NotificationBell />
              <Link to="/deposit" style={{ background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', padding: '6px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>💰 Deposit</Link>
              <Link to="/profile" style={{ display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--gradient-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: '#0a0900', overflow: 'hidden' }}>
                  {user.profile?.avatar
                    ? <img src={user.profile.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : user.username?.[0]?.toUpperCase()}
                </div>
                {user.vipTier && user.vipTier !== 'bronze' && <VipBadge tier={user.vipTier} size="sm" />}
              </Link>
              <button className="btn btn-secondary btn-sm" onClick={handleLogout}>Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-secondary btn-sm">Login</Link>
              <Link to="/register" className="btn btn-primary btn-sm">Sign Up</Link>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button onClick={() => setMenuOpen(!menuOpen)} style={{ display: 'none', background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 24, cursor: 'pointer' }} className="mobile-menu-btn">
          {menuOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div style={{ position: 'absolute', top: '70px', left: 0, right: 0, background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)', padding: '20px', display: 'flex', flexDirection: 'column', gap: 14, zIndex: 800 }}>
          {navLinks.map(([path, label]) => (
            <Link key={path} to={path} onClick={() => setMenuOpen(false)} style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 16 }}>{label}</Link>
          ))}
          <Link to="/deposit" onClick={() => setMenuOpen(false)} style={{ color: '#10b981', fontWeight: 700 }}>💰 Make Deposit</Link>
          <hr style={{ borderColor: 'var(--border)' }} />
          {user ? (
            <>
              {isAdmin && <Link to="/admin" onClick={() => setMenuOpen(false)} style={{ color: '#f5c400', fontWeight: 600 }}>Admin Panel</Link>}
              <Link to="/profile" onClick={() => setMenuOpen(false)} style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Profile</Link>
              <button className="btn btn-secondary" onClick={handleLogout}>Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-secondary" onClick={() => setMenuOpen(false)}>Login</Link>
              <Link to="/register" className="btn btn-primary" onClick={() => setMenuOpen(false)}>Sign Up</Link>
            </>
          )}
        </div>
      )}

      <style>{`
        @media (max-width: 1100px) { .desktop-nav { display: none !important; } .mobile-menu-btn { display: block !important; } }
      `}</style>
    </nav>
  );
}
